import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import useChannelChat from "../hooks/useChannelChat";
import { apiUrl, getApiBase } from "../lib/apiBase";
import { openExternalUrl } from "../lib/appMode";
import { resolveBadgeImage } from "../lib/badges";
import socket from "../lib/socket";
import GasPointsStoreModal from "./GasPointsStoreModal";
import {
  PLATFORM_EMOTES,
  PLATFORM_EMOTE_BY_CODE,
  PLATFORM_EMOTE_PATTERN,
} from "../lib/platformEmotes";

function getToken() {
  try {
    const fromStorage =
      localStorage.getItem("ga_token") ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("jwt") ||
      sessionStorage.getItem("ga_token") ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("authToken") ||
      sessionStorage.getItem("jwt") ||
      "";
    if (fromStorage) return fromStorage;
    const qs = new URLSearchParams(window.location.search);
    return qs.get("token") || qs.get("auth") || "";
  } catch {
    return "";
  }
}

function persistGasPointsBalance(balance) {
  const safeBalance = Math.max(0, Math.floor(Number(balance || 0)));
  for (const key of ["user", "ga_user", "member_user"]) {
    try {
      const current = JSON.parse(localStorage.getItem(key) || "null");
      if (!current || typeof current !== "object") continue;
      localStorage.setItem(key, JSON.stringify({ ...current, gasPoints: safeBalance }));
    } catch {
      // Ignore malformed or unavailable local storage.
    }
  }
  try {
    window.dispatchEvent(new CustomEvent("gas-points:changed", { detail: { balance: safeBalance } }));
    window.dispatchEvent(new Event("auth:changed"));
  } catch {
    // Ignore event dispatch failures outside a browser.
  }
}

const QUICK_EMOJIS = [
  "😂", "🤣", "🔥", "💯", "😤", "😈", "🫡", "🙏", "❤️", "😭",
  "🤝", "👀", "✅", "🎮", "🏀", "💰", "😎", "🗣️", "🧠", "👑",
];

function renderChatText(value = "") {
  return String(value || "")
    .split(PLATFORM_EMOTE_PATTERN)
    .filter(Boolean)
    .map((part, index) => {
      const emote = PLATFORM_EMOTE_BY_CODE.get(part);
      if (!emote) {
        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
      }

      return (
        <img
          key={`emote-${emote.key}-${index}`}
          className="chatbox-message-emote"
          src={emote.src}
          alt={emote.label}
          title={emote.label}
        />
      );
    });
}

const CHEER_PRESETS = [100, 500, 1000, 2500];

const REPORT_REASONS = [
  { value: "harassment", label: "Harassment or bullying" },
  { value: "hate_abuse", label: "Hate or abusive language" },
  { value: "spam_scam", label: "Spam, scam, or misleading content" },
  { value: "sexual_content", label: "Sexual or inappropriate content" },
  { value: "threats_violence", label: "Threats or violence" },
  { value: "other", label: "Other" },
];

/** Twitch/Kick-ish palette (stable + readable on dark bg) */
const NAME_COLORS = [
  "#5EA9FF",
  "#00D1B2",
  "#F7B731",
  "#E056FD",
  "#FF7675",
  "#55EFC4",
  "#FAB1A0",
  "#74B9FF",
  "#A29BFE",
  "#FD79A8",
];

/** Deterministic: same username -> same color */
function getNameColor(username = "") {
  const s = String(username || "").trim().toLowerCase();
  if (!s) return "rgba(255,255,255,0.95)";
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  return NAME_COLORS[Math.abs(hash) % NAME_COLORS.length];
}


function toAbsAvatarUrl(value = "") {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:")) return s;
  const base = getApiBase().replace(/\/+$/, "");
  return `${base}${s.startsWith("/") ? "" : "/"}${s}`;
}

function getInitial(name = "") {
  const clean = String(name || "").trim();
  if (!clean) return "?";
  return clean[0].toUpperCase();
}

function getChatIdentity(message, name, { isCheerBot = false, isAutoDj = false } = {}) {
  if (isAutoDj) return { type: "bot", label: "DJ", tone: "dj" };
  if (isCheerBot) return { type: "bot", label: "G", tone: "gas" };

  const badgeKey = message?.equippedBadgeKey || message?.user?.equippedBadgeKey || "";
  const customBadgeUrl = message?.customBadgeUrl || message?.user?.customBadgeUrl || "";
  const badgeSrc = resolveBadgeImage(badgeKey, customBadgeUrl, getApiBase());
  if (badgeSrc) return { type: "image", src: badgeSrc, label: getInitial(name) };

  return { type: "letter", label: getInitial(name) };
}

function parseCheerQuery() {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = String(params.get("cheer") || "").trim().toLowerCase();
    if (value === "success" || value === "canceled") return value;
    return "";
  } catch {
    return "";
  }
}


export default function ChatBox({
  room = "global",
  title = "CHAT",
  loginHref = "/login",
  role,
}) {
  const signedIn = !!getToken();

  const channelKey = useMemo(() => {
    const r = String(room || "").trim();
    const parts = r.split(":");
    const key = (parts.length > 1 ? parts[parts.length - 1] : r).trim();
    return key || "";
  }, [room]);

  const musicQueuePilot = useMemo(
    () => String(channelKey || "").trim().toLowerCase() === "getactive_pet3",
    [channelKey]
  );

  const [streamMeta, setStreamMeta] = useState({ title: "", game: "" });
  const [cheerOpen, setCheerOpen] = useState(false);
  const [cheerBits, setCheerBits] = useState(100);
  const [cheerMessage, setCheerMessage] = useState("");
  const [cheerBusy, setCheerBusy] = useState(false);
  const [cheerError, setCheerError] = useState("");
  const [cheerNotice, setCheerNotice] = useState(() => parseCheerQuery());
  const [cheerConfig, setCheerConfig] = useState({
    hasStripe: false,
    minBits: 50,
    maxBits: 100000,
    payoutReady: false,
    totalBits: 0,
  });
  const [pointsAccount, setPointsAccount] = useState(null);
  const [pointsOpen, setPointsOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [musicQueueOpen, setMusicQueueOpen] = useState(false);
  const [musicQueueCompact, setMusicQueueCompact] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 640px)").matches
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(max-width: 640px)");
    const sync = () => setMusicQueueCompact(query.matches);
    sync();
    if (query.addEventListener) {
      query.addEventListener("change", sync);
      return () => query.removeEventListener("change", sync);
    }
    query.addListener(sync);
    return () => query.removeListener(sync);
  }, []);

  useEffect(() => {
    if (!musicQueueOpen || !musicQueueCompact || typeof document === "undefined") return undefined;
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (event) => {
      if (event.key === "Escape") setMusicQueueOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [musicQueueOpen, musicQueueCompact]);
  const [musicQueueState, setMusicQueueState] = useState({
    enabled: false,
    queue: [],
    nowPlaying: null,
    source: "disabled",
    updatedAt: 0,
  });
  const [musicQueueSeStatus, setMusicQueueSeStatus] = useState({
    configured: false,
    connected: false,
    subscribed: false,
    socketConnected: false,
    displayName: "",
    lastError: "",
    canManage: false,
  });
  const musicQueueAuthoritative = String(musicQueueState?.source || "")
    .trim()
    .toLowerCase()
    .startsWith("streamelements-");
  const [musicQueueLinkBusy, setMusicQueueLinkBusy] = useState(false);
  const [musicQueueLinkNotice, setMusicQueueLinkNotice] = useState("");
  const [viewerData, setViewerData] = useState({
    count: 0,
    loggedInCount: 0,
    guestCount: 0,
    viewers: [],
  });
  const [currentUserId, setCurrentUserId] = useState("");
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [moderationTarget, setModerationTarget] = useState(null);
  const [reportType, setReportType] = useState("");
  const [reportReason, setReportReason] = useState("harassment");
  const [reportDetails, setReportDetails] = useState("");
  const [moderationBusy, setModerationBusy] = useState(false);
  const [moderationNotice, setModerationNotice] = useState("");

  useEffect(() => {
    let alive = true;
    if (!channelKey) return () => {};

    const load = async () => {
      try {
        const res = await fetch(`/api/streams/meta/${encodeURIComponent(channelKey)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (!alive) return;
        setStreamMeta({
          title: String(data?.meta?.title || data?.title || "").trim(),
          game: String(data?.meta?.game || data?.game || "").trim(),
        });
      } catch {
        // ignore
      }
    };

    load();
    const t = setInterval(load, 5000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [channelKey]);


  useEffect(() => {
    const activeRoom = String(room || "").trim();
    if (!activeRoom) return undefined;

    const onViewerUpdate = (data = {}) => {
      const eventRoom = String(data?.room || "").trim();
      if (eventRoom && eventRoom !== activeRoom) return;
      setViewerData((current) => ({
        ...current,
        count: Math.max(0, Number(data?.count || 0) || 0),
        loggedInCount:
          data?.loggedInCount === undefined
            ? current.loggedInCount
            : Math.max(0, Number(data.loggedInCount || 0) || 0),
        guestCount:
          data?.guestCount === undefined
            ? current.guestCount
            : Math.max(0, Number(data.guestCount || 0) || 0),
        viewers: Array.isArray(data?.viewers) ? data.viewers : current.viewers,
      }));
    };

    socket.on("viewers:update", onViewerUpdate);
    return () => socket.off("viewers:update", onViewerUpdate);
  }, [room]);

  useEffect(() => {
    let alive = true;
    const activeRoom = String(room || "").trim();
    if (!activeRoom || !viewerOpen) return () => {};

    const loadViewers = async () => {
      try {
        const res = await fetch(apiUrl(`/viewers?room=${encodeURIComponent(activeRoom)}`), {
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (!alive || !data?.ok) return;
        setViewerData({
          count: Math.max(0, Number(data.count || 0) || 0),
          loggedInCount: Math.max(0, Number(data.loggedInCount || 0) || 0),
          guestCount: Math.max(0, Number(data.guestCount || 0) || 0),
          viewers: Array.isArray(data.viewers) ? data.viewers : [],
        });
      } catch {
        // ignore viewer panel refresh errors
      }
    };

    loadViewers();
    const id = window.setInterval(loadViewers, 10_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [room, viewerOpen]);

  useEffect(() => {
    let alive = true;
    if (!musicQueuePilot || !channelKey) {
      setMusicQueueOpen(false);
      setMusicQueueState({
        enabled: false,
        queue: [],
        nowPlaying: null,
        source: "disabled",
        updatedAt: 0,
      });
      setMusicQueueSeStatus({
        configured: false,
        connected: false,
        subscribed: false,
        socketConnected: false,
        displayName: "",
        lastError: "",
        canManage: false,
      });
      return () => {
        alive = false;
      };
    }

    const applyQueueState = (data = {}) => {
      if (!alive || !data?.enabled) return;
      setMusicQueueState({
        enabled: true,
        queue: Array.isArray(data.queue) ? data.queue : [],
        nowPlaying: data?.nowPlaying || null,
        source: String(data?.source || "ga-chat-pilot"),
        updatedAt: Math.max(0, Number(data?.updatedAt || 0) || 0),
      });
      if (data?.connection) {
        setMusicQueueSeStatus((current) => ({
          ...current,
          connected: Boolean(data.connection.connected || current.connected),
          subscribed: Boolean(data.connection.subscribed),
          socketConnected: Boolean(data.connection.connected),
          displayName: String(data.connection.accountName || current.displayName || ""),
          lastError: String(data.connection.lastError || ""),
        }));
      }
    };

    const loadQueue = async () => {
      try {
        const response = await fetch(
          apiUrl(`/obs-music-queue/${encodeURIComponent(channelKey)}`),
          { cache: "no-store" }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.ok) return;
        applyQueueState(data);
      } catch {
        // Queue display is optional; never disturb chat if it is unavailable.
      }
    };

    const loadSeStatus = async () => {
      try {
        const token = getToken();
        const response = await fetch(
          apiUrl(`/se/status/${encodeURIComponent(channelKey)}`),
          {
            cache: "no-store",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        const data = await response.json().catch(() => ({}));
        if (!alive || !response.ok || !data?.ok) return;
        setMusicQueueSeStatus({
          configured: Boolean(data.configured),
          connected: Boolean(data.connected),
          subscribed: Boolean(data.subscribed),
          socketConnected: Boolean(data.socketConnected),
          displayName: String(data.displayName || ""),
          lastError: String(data.lastError || ""),
          canManage: Boolean(data.canManage),
        });
      } catch {
        // StreamElements status is optional and must never break chat.
      }
    };

    const onQueueUpdate = (data = {}) => {
      const eventSlug = String(data?.channelSlug || "").trim().toLowerCase();
      if (eventSlug && eventSlug !== String(channelKey).trim().toLowerCase()) return;
      applyQueueState(data);
    };

    loadQueue();
    loadSeStatus();
    socket.on("obs-music-queue:update", onQueueUpdate);
    const id = window.setInterval(() => {
      loadQueue();
      loadSeStatus();
    }, 8000);

    return () => {
      alive = false;
      socket.off("obs-music-queue:update", onQueueUpdate);
      window.clearInterval(id);
    };
  }, [channelKey, musicQueuePilot, signedIn]);

  const connectStreamElements = useCallback(async () => {
    if (!musicQueuePilot || musicQueueLinkBusy) return;
    setMusicQueueLinkBusy(true);
    setMusicQueueLinkNotice("");
    try {
      const response = await fetch(apiUrl("/se/oauth/connect-url"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ username: "getactive_pet3" }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.url) {
        throw new Error(
          data?.message ||
            (data?.error === "streamelements_oauth_not_configured"
              ? "StreamElements OAuth is not configured on the server yet."
              : "Could not start StreamElements connection.")
        );
      }
      window.location.assign(data.url);
    } catch (error) {
      setMusicQueueLinkNotice(
        error?.message || "Could not connect StreamElements right now."
      );
      setMusicQueueLinkBusy(false);
    }
  }, [musicQueuePilot, musicQueueLinkBusy]);

  useEffect(() => {
    let alive = true;

    const loadBlockedUsers = async () => {
      if (!signedIn) {
        if (alive) {
          setCurrentUserId("");
          setBlockedUsers([]);
        }
        return;
      }

      try {
        const response = await fetch(apiUrl("/chat/moderation/blocked"), {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.message || "Unable to load blocked users.");
        if (!alive) return;
        setCurrentUserId(String(data?.currentUserId || ""));
        setBlockedUsers(Array.isArray(data?.blocked) ? data.blocked : []);
      } catch {
        if (!alive) return;
        setBlockedUsers([]);
      }
    };

    loadBlockedUsers();
    return () => {
      alive = false;
    };
  }, [signedIn]);

  useEffect(() => {
    if (!moderationNotice) return undefined;
    const id = window.setTimeout(() => setModerationNotice(""), 4200);
    return () => window.clearTimeout(id);
  }, [moderationNotice]);

  useEffect(() => {
    let alive = true;

    const loadCheerConfig = async () => {
      try {
        const [statusRes, summaryRes] = await Promise.allSettled([
          fetch(apiUrl("/cheers/status")),
          channelKey ? fetch(apiUrl(`/cheers/summary/${encodeURIComponent(channelKey)}`)) : Promise.resolve(null),
        ]);

        let next = {
          hasStripe: false,
          minBits: 50,
          maxBits: 100000,
          payoutReady: false,
          totalBits: 0,
        };

        if (statusRes.status === "fulfilled" && statusRes.value?.ok) {
          const status = await statusRes.value.json().catch(() => ({}));
          next = {
            ...next,
            hasStripe: !!status?.hasStripe,
            minBits: Math.max(1, Number(status?.minBits || 50) || 50),
            maxBits: Math.max(1, Number(status?.maxBits || 100000) || 100000),
          };
        }

        if (summaryRes.status === "fulfilled" && summaryRes.value?.ok) {
          const summaryData = await summaryRes.value.json().catch(() => ({}));
          next = {
            ...next,
            payoutReady: !!summaryData?.payoutReady,
            totalBits: Math.max(0, Number(summaryData?.summary?.totalBits || 0) || 0),
          };
        }

        if (!alive) return;
        setCheerConfig(next);
        setCheerBits((prev) => {
          const safePrev = Math.floor(Number(prev || 0));
          if (!safePrev) return next.minBits;
          return Math.min(Math.max(safePrev, next.minBits), next.maxBits);
        });
      } catch {
        if (!alive) return;
        setCheerConfig((prev) => ({ ...prev }));
      }
    };

    loadCheerConfig();
    return () => {
      alive = false;
    };
  }, [channelKey]);

  const applyPointsAccount = useCallback((nextAccount) => {
    if (!nextAccount || typeof nextAccount !== "object") return;
    setPointsAccount(nextAccount);
    persistGasPointsBalance(nextAccount.balance);
  }, []);

  useEffect(() => {
    let alive = true;
    if (!signedIn) {
      setPointsAccount(null);
      return () => { alive = false; };
    }

    fetch(apiUrl("/gas-points/me"), {
      headers: { Authorization: `Bearer ${getToken()}` },
      cache: "no-store",
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.ok || !alive) return;
        applyPointsAccount(data.account);
      })
      .catch(() => {});

    return () => { alive = false; };
  }, [signedIn, applyPointsAccount]);

  useEffect(() => {
    const onBalance = (payload = {}) => {
      if (!pointsAccount?.userId) return;
      if (
        payload?.userId &&
        String(payload.userId) !== String(pointsAccount.userId)
      ) {
        return;
      }
      const nextBalance = Math.max(0, Math.floor(Number(payload?.balance || 0)));
      setPointsAccount((current) =>
        current ? { ...current, balance: nextBalance } : current
      );
      persistGasPointsBalance(nextBalance);
    };
    socket.on("gas-points:balance", onBalance);
    return () => socket.off("gas-points:balance", onBalance);
  }, [pointsAccount?.userId]);

  const roleFromUrl = useMemo(() => {
    try {
      const v = new URLSearchParams(window.location.search).get("role");
      return v ? String(v).trim() : "";
    } catch {
      return "";
    }
  }, []);

  const roleToSend = (role && String(role).trim()) || roleFromUrl || "";

  const { status, messages, send } = useChannelChat(room, {
    canPost: signedIn,
    role: roleToSend,
  });

  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const listRef = useRef(null);
  const messageInputRef = useRef(null);

  const blockedUserIdSet = useMemo(
    () => new Set(blockedUsers.map((item) => String(item?.userId || "")).filter(Boolean)),
    [blockedUsers]
  );

  const visibleMessages = useMemo(
    () =>
      messages.filter((message) => {
        const messageUserId = String(message?.user?.id || "").trim();
        return !messageUserId || !blockedUserIdSet.has(messageUserId);
      }),
    [messages, blockedUserIdSet]
  );

  const [sfxOn, setSfxOn] = useState(() => {
    const v = localStorage.getItem("ga_chat_sfx");
    return v === null ? true : v !== "0";
  });

  const audioRef = useRef({ ctx: null });

  const ensureAudio = useCallback(() => {
    try {
      if (!audioRef.current.ctx) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioRef.current.ctx = new Ctx();
      }
      const ctx = audioRef.current.ctx;
      if (ctx && ctx.state === "suspended") {
        ctx.resume().catch(() => {});
      }
      return ctx;
    } catch {
      return null;
    }
  }, []);

  const playBeep = useCallback(
    (type = "msg") => {
      if (!sfxOn) return;
      const ctx = ensureAudio();
      if (!ctx || ctx.state !== "running") return;

      const o = ctx.createOscillator();
      const g = ctx.createGain();

      const freq = type === "send" ? 740 : type === "cheer" ? 880 : 520;
      o.type = type === "cheer" ? "triangle" : "sine";
      o.frequency.value = freq;

      const now = ctx.currentTime;
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.16, now + 0.01);
      g.gain.exponentialRampToValueAtTime(
        0.0001,
        now + (type === "send" ? 0.06 : type === "cheer" ? 0.16 : 0.09)
      );

      o.connect(g);
      g.connect(ctx.destination);

      o.start(now);
      o.stop(now + (type === "send" ? 0.07 : type === "cheer" ? 0.18 : 0.11));
    },
    [sfxOn, ensureAudio]
  );

  const headerRight = useMemo(() => {
    if (!signedIn) return "";
    if (status === "live") return "LIVE";
    if (status === "connecting") return "CONNECTING…";
    return "OFFLINE";
  }, [signedIn, status]);

  const metaLine = useMemo(() => {
    const g = String(streamMeta?.game || "").trim();
    const t = String(streamMeta?.title || "").trim();
    if (!g && !t) return "";
    if (g && t) return `${g} • ${t}`;
    return g || t;
  }, [streamMeta]);

  const cheerChannelName = useMemo(() => {
    return String(streamMeta?.title || title || channelKey || "Stream").trim();
  }, [streamMeta, title, channelKey]);

  const cheerButtonDisabled = useMemo(() => {
    return !signedIn || !cheerConfig?.hasStripe || !cheerConfig?.payoutReady || cheerBusy;
  }, [signedIn, cheerConfig, cheerBusy]);

  const cheerButtonTitle = useMemo(() => {
    if (!signedIn) return "Sign in to send GAS Cheers";
    if (!cheerConfig?.hasStripe) return "GAS Cheers are unavailable right now";
    if (!cheerConfig?.payoutReady) return "This streamer is not ready to receive GAS Cheers yet";
    return "Send GAS Cheers to this streamer";
  }, [signedIn, cheerConfig]);

  const gasPointsBalance = Math.max(0, Number(pointsAccount?.balance || 0));
  const gasChatEmoteCost = Math.max(
    0,
    Number(pointsAccount?.config?.chatEmoteCost || 25)
  );
  const gasChatEmotesFree = Boolean(pointsAccount?.memberActive);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [visibleMessages.length]);

  const prevLenRef = useRef(null);
  useEffect(() => {
    if (prevLenRef.current === null) {
      prevLenRef.current = visibleMessages.length;
      return;
    }
    if (visibleMessages.length > prevLenRef.current) {
      const last = visibleMessages[visibleMessages.length - 1];
      const isCheerBot =
        ["cheerbot", "gasbot"].includes(String(last?.username || last?.user?.name || "").trim().toLowerCase());
      playBeep(isCheerBot ? "cheer" : "msg");
    }
    prevLenRef.current = visibleMessages.length;
  }, [visibleMessages, playBeep]);

  const keepComposerFocused = useCallback(() => {
    try {
      const inputEl = messageInputRef.current;
      if (!inputEl || document.activeElement === inputEl) return;
      window.requestAnimationFrame(() => {
        try {
          inputEl.focus({ preventScroll: true });
        } catch {
          inputEl.focus();
        }
      });
    } catch {
      // ignore focus restore failures
    }
  }, []);

  // Auto-grow the composer vertically so long messages remain visible while
  // typing on Android, iOS/WKWebView, and desktop. Cap it so chat still has
  // room; after the cap, the textarea itself scrolls.
  useEffect(() => {
    const inputEl = messageInputRef.current;
    if (!inputEl) return undefined;

    const resize = () => {
      const minHeight = 40;
      const cssMaxHeight = Number.parseFloat(
        window.getComputedStyle(inputEl).maxHeight || ""
      );
      const maxHeight =
        Number.isFinite(cssMaxHeight) && cssMaxHeight > 0
          ? cssMaxHeight
          : 96;

      // Keep the composer at its normal one-line height until the user
      // actually starts typing. Some mobile WebViews include wrapped
      // placeholder text in scrollHeight, which made the empty box look
      // two-lines tall before input began.
      if (!String(text || "").length) {
        inputEl.style.height = `${minHeight}px`;
        inputEl.style.overflowY = "hidden";
        return;
      }

      inputEl.style.height = "auto";
      const scrollHeight = Number(inputEl.scrollHeight || minHeight);
      const nextHeight = Math.max(
        minHeight,
        Math.min(maxHeight, scrollHeight)
      );
      inputEl.style.height = `${nextHeight}px`;
      inputEl.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
    };

    const frame = window.requestAnimationFrame(resize);
    return () => window.cancelAnimationFrame(frame);
  }, [text]);

  const insertEmoji = (e) => {
    setText((t) => `${t}${e}`);
    setShowEmoji(false);
    keepComposerFocused();
  };

  const insertPlatformEmote = (code) => {
    if (!gasChatEmotesFree && gasPointsBalance < gasChatEmoteCost) {
      setShowEmoji(false);
      setPointsOpen(true);
      return;
    }
    setText((current) => {
      const spacer = current && !/\s$/.test(current) ? " " : "";
      return `${current}${spacer}${code} `;
    });
    setShowEmoji(false);
    keepComposerFocused();
  };

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    playBeep("send");
    send(t);
    setText("");
    setShowEmoji(false);
    setMobileTypingMode(true);
    keepComposerFocused();
  };

  const closeModeration = useCallback(() => {
    if (moderationBusy) return;
    setModerationTarget(null);
    setReportType("");
    setReportReason("harassment");
    setReportDetails("");
  }, [moderationBusy]);

  const blockModerationTarget = useCallback(async () => {
    if (!moderationTarget?.userId || moderationBusy) return;
    const confirmed = window.confirm(
      `Block ${moderationTarget.username}? Their chat messages will be hidden from you until you unblock them.`
    );
    if (!confirmed) return;

    setModerationBusy(true);
    try {
      const response = await fetch(apiUrl("/chat/moderation/block"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ targetUserId: moderationTarget.userId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Unable to block that user.");
      const blocked = data?.blocked || {
        userId: moderationTarget.userId,
        username: moderationTarget.username,
        blockedAt: Date.now(),
      };
      setBlockedUsers((prev) => {
        const next = prev.filter((item) => String(item?.userId || "") !== String(blocked.userId || ""));
        return [...next, blocked];
      });
      setModerationNotice(`${moderationTarget.username} is now blocked in chat.`);
      setModerationTarget(null);
      setReportType("");
      setReportDetails("");
    } catch (err) {
      setModerationNotice(err?.message || "Unable to block that user right now.");
    } finally {
      setModerationBusy(false);
    }
  }, [moderationBusy, moderationTarget]);

  const unblockUser = useCallback(async (targetUserId, username) => {
    if (!targetUserId || moderationBusy) return;
    setModerationBusy(true);
    try {
      const response = await fetch(
        apiUrl(`/chat/moderation/block/${encodeURIComponent(targetUserId)}`),
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Unable to unblock that user.");
      setBlockedUsers((prev) =>
        prev.filter((item) => String(item?.userId || "") !== String(targetUserId))
      );
      setModerationNotice(`${username || "User"} is unblocked.`);
    } catch (err) {
      setModerationNotice(err?.message || "Unable to unblock that user right now.");
    } finally {
      setModerationBusy(false);
    }
  }, [moderationBusy]);

  const submitReport = useCallback(async () => {
    if (!moderationTarget?.userId || !reportType || moderationBusy) return;
    setModerationBusy(true);
    try {
      const response = await fetch(apiUrl("/chat/moderation/report"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          type: reportType,
          reason: reportReason,
          details: reportDetails,
          targetUserId: moderationTarget.userId,
          targetUsername: moderationTarget.username,
          room,
          messageId: moderationTarget.messageId,
          messageText: moderationTarget.messageText,
          messageTs: moderationTarget.messageTs,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.message || "Unable to submit that report.");
      setModerationNotice(data?.message || "Report submitted. GAStreaming will review it.");
      setModerationTarget(null);
      setReportType("");
      setReportReason("harassment");
      setReportDetails("");
    } catch (err) {
      setModerationNotice(err?.message || "Unable to submit that report right now.");
    } finally {
      setModerationBusy(false);
    }
  }, [moderationTarget, reportType, reportReason, reportDetails, moderationBusy, room]);

  const setMobileTypingMode = useCallback((active) => {
    try {
      const inputEl = messageInputRef.current;
      const streamPage = inputEl?.closest?.(".stream-page");
      if (streamPage) {
        streamPage.classList.toggle("mobile-chat-active", !!active);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    return () => {
      setMobileTypingMode(false);
    };
  }, [setMobileTypingMode]);

  const submitCheer = async () => {
    const bits = Math.floor(Number(cheerBits || 0));
    const token = getToken();

    if (!signedIn || !token) {
      window.location.href = loginHref;
      return;
    }

    if (!channelKey) {
      setCheerError("Unable to determine the streamer for this GAS.");
      return;
    }

    if (!cheerConfig?.hasStripe) {
      setCheerError("GAS is unavailable right now.");
      return;
    }

    if (!cheerConfig?.payoutReady) {
      setCheerError("This streamer is not set up to receive GAS yet.");
      return;
    }

    if (!bits || bits < cheerConfig.minBits || bits > cheerConfig.maxBits) {
      setCheerError(`Enter between ${cheerConfig.minBits} and ${cheerConfig.maxBits} GAS.`);
      return;
    }

    setCheerBusy(true);
    setCheerError("");

    try {
      const res = await fetch(apiUrl("/cheers/create-checkout-session"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          channelSlug: channelKey,
          channelName: cheerChannelName,
          bits,
          message: String(cheerMessage || "").trim().slice(0, 500),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data?.ok || !data?.url) {
        throw new Error(data?.error || data?.message || "Unable to start GAS checkout.");
      }

      await openExternalUrl(data.url);
    } catch (err) {
      setCheerBusy(false);
      setCheerError(err?.message || "Unable to start GAS checkout.");
    }
  };

  const musicQueueDialog = (
        <div
          role="dialog"
          aria-modal={musicQueueCompact ? "true" : undefined}
          aria-label="Music queue"
          onClick={(event) => event.stopPropagation()}
          style={{
            position: musicQueueCompact ? "relative" : "absolute",
            top: musicQueueCompact ? "auto" : 52,
            right: musicQueueCompact ? "auto" : 10,
            zIndex: musicQueueCompact ? 1 : 67,
            width: musicQueueCompact ? "100%" : 330,
            maxWidth: musicQueueCompact ? 420 : "calc(100% - 20px)",
            height: musicQueueCompact ? "min(76dvh, 570px)" : "auto",
            maxHeight: musicQueueCompact ? "100%" : undefined,
            display: musicQueueCompact ? "flex" : "block",
            flexDirection: musicQueueCompact ? "column" : undefined,
            overflow: musicQueueCompact ? "hidden" : "visible",
            boxSizing: "border-box",
            textTransform: "none",
            borderRadius: 16,
            border: "1px solid rgba(130,220,255,0.18)",
            background: "linear-gradient(180deg, rgba(8,12,20,0.98), rgba(14,4,18,0.97))",
            boxShadow: "0 18px 48px rgba(0,0,0,0.58)",
            padding: 12,
            color: "white",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16, fontWeight: 900 }}>🎵 Music Queue</span>
              <span style={{ fontSize: 11, opacity: 0.75, padding: "2px 7px", borderRadius: 99, background: "rgba(255,255,255,0.08)" }}>
                {Array.isArray(musicQueueState.queue) ? musicQueueState.queue.length : 0} upcoming
              </span>
            </div>
            <button
              type="button"
              onClick={() => setMusicQueueOpen(false)}
              style={{
                border: "none",
                background: "rgba(255,255,255,0.07)",
                color: "white",
                borderRadius: 999,
                width: 34,
                height: 34,
                fontSize: 20,
                cursor: "pointer",
                fontWeight: 950,
                flex: "0 0 auto",
              }}
              aria-label="Close music queue"
            >
              ×
            </button>
          </div>

          <div
            className="chatbox-music-queue-content"
            style={musicQueueCompact ? {
              flex: "1 1 auto",
              minHeight: 0,
              overflowY: "auto",
              overscrollBehavior: "contain",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              paddingRight: 2,
              paddingBottom: 12,
              scrollbarWidth: "thin",
            } : {}}
          >
          {!musicQueueAuthoritative ? (
            <div style={{ marginTop: 9, padding: "8px 9px", borderRadius: 10, background: "rgba(255,255,255,0.05)", fontSize: 12 }}>
              {musicQueueSeStatus.subscribed
                ? "Syncing music queue…"
                : musicQueueSeStatus.connected
                ? "Reconnecting music queue…"
                : "Connecting music queue…"}
              {musicQueueSeStatus.canManage && !musicQueueSeStatus.subscribed ? (
                <button
                  type="button"
                  onClick={connectStreamElements}
                  disabled={musicQueueLinkBusy || !musicQueueSeStatus.configured}
                  style={{ display: "block", width: "100%", minHeight: 36, marginTop: 8, borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.14)", background: "linear-gradient(135deg, rgba(112,55,255,0.86), rgba(0,145,255,0.82))",
                    color: "white", fontWeight: 800, cursor: "pointer", opacity: musicQueueSeStatus.configured ? 1 : 0.6 }}
                >
                  {musicQueueLinkBusy ? "Opening StreamElements…" : "Connect StreamElements"}
                </button>
              ) : null}
              {musicQueueLinkNotice ? <div style={{ marginTop: 6, color: "#ffb4b4", fontWeight: 800 }}>{musicQueueLinkNotice}</div> : null}
            </div>
          ) : null}

          {musicQueueState.nowPlaying ? (
            <div
              style={{
                marginTop: 10,
                padding: "10px 11px",
                borderRadius: 12,
                border: "1px solid rgba(124,112,255,0.20)",
                background: "linear-gradient(135deg, rgba(95,70,255,0.12), rgba(0,170,255,0.08))",
              }}
            >
              <div style={{ fontSize: 9, opacity: 0.62, fontWeight: 950, letterSpacing: 0.8 }}>
                Now playing
              </div>
              <div style={{ marginTop: 4, fontSize: 13, fontWeight: 950, overflowWrap: "anywhere" }}>
                {musicQueueState.nowPlaying.input || "StreamElements media"}
              </div>
              {musicQueueState.nowPlaying.requestedBy ? (
                <div style={{ marginTop: 3, fontSize: 10, opacity: 0.65 }}>
                  Requested by {musicQueueState.nowPlaying.requestedBy}
                </div>
              ) : null}
            </div>
          ) : null}

          {Array.isArray(musicQueueState.queue) && musicQueueState.queue.length ? (
            <div
              style={{
                display: "grid",
                gap: 7,
                marginTop: 10,
                maxHeight: musicQueueCompact ? "none" : 290,
                overflowY: musicQueueCompact ? "visible" : "auto",
                paddingRight: 2,
              }}
            >
              {musicQueueState.queue.map((item, index) => (
                <div
                  key={item?.id || `${item?.input || "song"}-${index}`}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "30px minmax(0,1fr)",
                    gap: 9,
                    alignItems: "center",
                    padding: "9px 10px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.08)",
                    background: index === 0
                      ? "linear-gradient(135deg, rgba(0,160,255,0.12), rgba(120,60,255,0.08))"
                      : "rgba(255,255,255,0.035)",
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 999,
                      display: "grid",
                      placeItems: "center",
                      background: "rgba(255,255,255,0.08)",
                      fontWeight: 950,
                      fontSize: 12,
                    }}
                  >
                    {index + 1}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 900,
                        fontSize: 13,
                        lineHeight: 1.25,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item?.input || "Song request"}
                    </div>
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 10,
                        opacity: 0.62,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Requested by {item?.requestedBy || "GA member"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                marginTop: 12,
                padding: "18px 10px",
                textAlign: "center",
                borderRadius: 12,
                border: "1px dashed rgba(255,255,255,0.12)",
                opacity: 0.72,
                fontSize: 12,
              }}
            >
              {musicQueueAuthoritative || musicQueueSeStatus.subscribed
                ? musicQueueState.nowPlaying
                  ? "No viewer requests waiting after the current song."
                  : "No viewer requests waiting. A StreamElements backup playlist can still play when the request queue is empty."
                : "Waiting for the live StreamElements Media Request state."}
            </div>
          )}
          </div>
        </div>
  );

  return (
    <div className="chatbox" style={{ position: "relative" }}>
      <div
        className="chat-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 12px",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          background: "rgba(0,0,0,0.30)",
        }}
      >
        <div style={{ fontWeight: 900, letterSpacing: 0.6, display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
            {metaLine ? (
              <span
                style={{
                  fontWeight: 800,
                  fontSize: 13,
                  opacity: 0.95,
                  letterSpacing: 0.2,
                }}
              >
                {metaLine}
              </span>
            ) : (
              <span>{title}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              ensureAudio();
              setSfxOn((v) => {
                const next = !v;
                localStorage.setItem("ga_chat_sfx", next ? "1" : "0");
                return next;
              });
            }}
            title={sfxOn ? "Chat sounds: ON" : "Chat sounds: OFF"}
            style={{
              width: 34,
              height: 28,
              display: "grid",
              placeItems: "center",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "white",
              cursor: "pointer",
              opacity: signedIn ? 1 : 0.75,
              fontWeight: 900,
            }}
          >
            {sfxOn ? "🔔" : "🔕"}
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {signedIn && (
            <button
              type="button"
              onClick={() => {
                setViewerOpen(false);
                setMusicQueueOpen(false);
                setBlockedOpen((value) => !value);
              }}
              title="Blocked users"
              aria-label="Manage blocked users"
              style={{
                height: 30,
                minWidth: 36,
                padding: "0 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                borderRadius: 999,
                border: blockedOpen
                  ? "1px solid rgba(255,90,90,0.44)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: blockedOpen ? "rgba(220,45,45,0.18)" : "rgba(255,255,255,0.06)",
                color: "white",
                cursor: "pointer",
                fontWeight: 950,
                fontSize: 12,
              }}
            >
              <span aria-hidden="true">🛡️</span>
              {blockedUsers.length > 0 ? <span>{blockedUsers.length}</span> : null}
            </button>
          )}

          {musicQueuePilot && (
            <button
              type="button"
              onClick={() => {
                setBlockedOpen(false);
                setViewerOpen(false);
                setMusicQueueOpen((value) => !value);
              }}
              title="Music request queue"
              aria-label="Open music request queue"
              style={{
                height: 30,
                minWidth: 46,
                padding: "0 8px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 4,
                borderRadius: 999,
                border: musicQueueOpen
                  ? "1px solid rgba(110,220,255,0.45)"
                  : "1px solid rgba(255,255,255,0.12)",
                background: musicQueueOpen
                  ? "linear-gradient(135deg, rgba(0,160,255,0.24), rgba(120,60,255,0.22))"
                  : "rgba(255,255,255,0.06)",
                color: "white",
                cursor: "pointer",
                fontWeight: 950,
                fontSize: 12,
                boxShadow: musicQueueOpen ? "0 8px 22px rgba(0,0,0,0.28)" : "none",
              }}
            >
              <span aria-hidden="true">🎵</span>
              <span>{
                (Array.isArray(musicQueueState.queue) ? musicQueueState.queue.length : 0) +
                (musicQueueState.nowPlaying ? 1 : 0)
              }</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => {
              setBlockedOpen(false);
              setMusicQueueOpen(false);
              setViewerOpen((v) => !v);
            }}
            title="Community watching now"
            style={{
              height: 30,
              minWidth: 50,
              padding: "0 9px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              borderRadius: 999,
              border: viewerOpen
                ? "1px solid rgba(255,255,255,0.30)"
                : "1px solid rgba(255,255,255,0.12)",
              background: viewerOpen
                ? "linear-gradient(135deg, rgba(0,160,255,0.24), rgba(255,70,180,0.20))"
                : "rgba(255,255,255,0.06)",
              color: "white",
              cursor: "pointer",
              fontWeight: 950,
              fontSize: 12,
              boxShadow: viewerOpen ? "0 8px 22px rgba(0,0,0,0.28)" : "none",
            }}
          >
            <span aria-hidden="true">👥</span>
            <span>{Number(viewerData.count || 0)}</span>
          </button>

          {!signedIn ? (
            <button
              type="button"
              onClick={() => {
                window.location.href = loginHref;
              }}
              style={{
                fontWeight: 900,
                opacity: 0.95,
                background: "transparent",
                border: "none",
                color: "white",
                cursor: "pointer",
                padding: 0,
                margin: 0,
                letterSpacing: 0.6,
              }}
              title="Sign in to chat"
            >
              SIGN IN
            </button>
          ) : (
            <div style={{ fontWeight: 900, opacity: 0.9 }}>{headerRight}</div>
          )}
        </div>
      </div>

      {blockedOpen && signedIn && (
        <div
          style={{
            position: "absolute",
            top: 52,
            right: 10,
            zIndex: 66,
            width: 300,
            maxWidth: "calc(100% - 20px)",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "linear-gradient(180deg, rgba(12,12,18,0.98), rgba(20,0,8,0.96))",
            boxShadow: "0 18px 48px rgba(0,0,0,0.58)",
            padding: 12,
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 950, letterSpacing: 0.4 }}>Blocked users</div>
              <div style={{ fontSize: 11, opacity: 0.72, marginTop: 2 }}>
                Their chat messages stay hidden from you.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBlockedOpen(false)}
              style={{
                border: "none",
                background: "rgba(255,255,255,0.07)",
                color: "white",
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: "pointer",
                fontWeight: 950,
              }}
              aria-label="Close blocked users panel"
            >
              ×
            </button>
          </div>

          {blockedUsers.length === 0 ? (
            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.76 }}>You have not blocked anyone.</div>
          ) : (
            <div style={{ display: "grid", gap: 8, marginTop: 12, maxHeight: 260, overflowY: "auto" }}>
              {blockedUsers.map((item) => (
                <div
                  key={item.userId}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "9px 10px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.09)",
                    background: "rgba(255,255,255,0.05)",
                  }}
                >
                  <span style={{ minWidth: 0, fontSize: 12, fontWeight: 850, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.username || "Blocked User"}
                  </span>
                  <button
                    type="button"
                    onClick={() => unblockUser(item.userId, item.username)}
                    disabled={moderationBusy}
                    style={{
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.07)",
                      color: "white",
                      borderRadius: 9,
                      padding: "6px 9px",
                      cursor: moderationBusy ? "wait" : "pointer",
                      fontWeight: 900,
                      fontSize: 11,
                    }}
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {viewerOpen && (
        <div
          style={{
            position: "absolute",
            top: 52,
            right: 10,
            zIndex: 65,
            width: 285,
            maxWidth: "calc(100% - 20px)",
            borderRadius: 16,
            border: "1px solid rgba(255,255,255,0.14)",
            background: "linear-gradient(180deg, rgba(12,12,18,0.97), rgba(20,0,8,0.94))",
            boxShadow: "0 18px 48px rgba(0,0,0,0.55)",
            padding: 12,
            color: "white",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 950, letterSpacing: 0.5 }}>Community</div>
              <div style={{ fontSize: 11, opacity: 0.72, marginTop: 2 }}>Watching this room now</div>
            </div>
            <button
              type="button"
              onClick={() => setViewerOpen(false)}
              style={{
                border: "none",
                background: "rgba(255,255,255,0.07)",
                color: "white",
                borderRadius: 999,
                width: 26,
                height: 26,
                cursor: "pointer",
                fontWeight: 950,
              }}
              aria-label="Close community panel"
            >
              ×
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 7,
              marginTop: 11,
            }}
          >
            {[
              ["Total", viewerData.count],
              ["Signed in", viewerData.loggedInCount],
              ["Guests", viewerData.guestCount],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  borderRadius: 12,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  padding: "8px 6px",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: 15, fontWeight: 950 }}>{Number(value || 0)}</div>
                <div style={{ fontSize: 9, opacity: 0.72, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 12, maxHeight: 210, overflowY: "auto", paddingRight: 2 }}>
            {Array.isArray(viewerData.viewers) && viewerData.viewers.length ? (
              viewerData.viewers.map((viewer) => {
                const name = String(viewer?.name || "Viewer").trim();
                const avatar = String(viewer?.avatarUrl || "").trim();
                return (
                  <div
                    key={viewer?.userId || name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      padding: "7px 6px",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.035)",
                      marginBottom: 6,
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        overflow: "hidden",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, rgba(255,255,255,0.14), rgba(255,0,70,0.18))",
                        border: "1px solid rgba(255,255,255,0.14)",
                        color: "white",
                        fontSize: 12,
                        fontWeight: 950,
                        flex: "0 0 auto",
                      }}
                    >
                      {avatar ? (
                        <img
                          src={toAbsAvatarUrl(avatar)}
                          alt=""
                          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", borderRadius: "50%" }}
                          onError={(e) => {
                            const parent = e.currentTarget.parentElement;
                            e.currentTarget.remove();
                            if (parent) parent.textContent = getInitial(name);
                          }}
                        />
                      ) : (
                        getInitial(name)
                      )}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 900,
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {name}
                      </div>
                      {Number(viewer?.sockets || 0) > 1 ? (
                        <div style={{ fontSize: 10, opacity: 0.62 }}>{viewer.sockets} tabs open</div>
                      ) : null}
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ fontSize: 12, opacity: 0.72, padding: "8px 2px" }}>
                No signed-in viewers detected yet. Guests are counted without names.
              </div>
            )}
          </div>
        </div>
      )}

      {musicQueuePilot && musicQueueOpen && (musicQueueCompact && typeof document !== "undefined"
        ? createPortal(
            <div
              role="presentation"
              onClick={() => setMusicQueueOpen(false)}
              style={{ position: "fixed", inset: 0, zIndex: 10000,
                background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center",
                justifyContent: "center", boxSizing: "border-box",
                padding: "max(12px, env(safe-area-inset-top, 0px)) 12px max(12px, env(safe-area-inset-bottom, 0px))" }}
            >
              {musicQueueDialog}
            </div>,
            document.body
          )
        : musicQueueDialog)}

      {cheerNotice ? (
        <div
          style={{
            marginTop: 10,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.10)",
            background:
              cheerNotice === "success"
                ? "linear-gradient(135deg, rgba(0,200,120,0.16), rgba(0,120,255,0.12))"
                : "linear-gradient(135deg, rgba(255,170,0,0.14), rgba(255,255,255,0.06))",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 800, opacity: 0.96 }}>
              {cheerNotice === "success"
                ? "GAS payment completed. It will show in chat as soon as Stripe confirms it."
                : "GAS checkout was canceled."}
            </div>
            <button
              type="button"
              onClick={() => setCheerNotice("")}
              style={{
                border: "none",
                background: "transparent",
                color: "white",
                fontWeight: 900,
                cursor: "pointer",
                opacity: 0.85,
              }}
            >
              ✕
            </button>
          </div>
        </div>
      ) : null}

      <div
        ref={listRef}
        className="chatbox-list"
        style={{
          marginTop: 10,
          borderRadius: 12,
          background: "rgba(0,0,0,0.20)",
          border: "1px solid rgba(255,255,255,0.10)",
          padding: 10,
        }}
      >
        {visibleMessages.length === 0 ? (
          <div style={{ opacity: 0.75 }}>No messages yet.</div>
        ) : (
          visibleMessages.map((m, idx) => {
            const name =
              m?.username ||
              m?.user?.displayName ||
              m?.user?.username ||
              m?.user?.name ||
              m?.user?.email ||
              "Member";
            const msg = m?.text || m?.msg || "";
            const lowerName = String(name).trim().toLowerCase();
            const isCheerBot = lowerName === "cheerbot" || lowerName === "gasbot";
            const isAutoDj = lowerName === "dj getactive";
            const isSystem = lowerName === "system";
            // The top-level message username is the display name for chat styling.
            // Public profile routes must use the account's canonical username.
            // Example: display name "Lost" can belong to username "StoryOfLost".
            const profileUsername = String(
              m?.user?.username || m?.username || ""
            ).trim();
            const canOpenProfile = Boolean(
              profileUsername && !isCheerBot && !isAutoDj && !isSystem
            );
            const targetUserId = String(m?.user?.id || "").trim();
            const canModerateMessage = Boolean(
              signedIn &&
                targetUserId &&
                targetUserId !== String(currentUserId || "") &&
                !isCheerBot &&
                !isAutoDj &&
                !isSystem
            );
            const avatar = getChatIdentity(m, name, { isCheerBot, isAutoDj });
            const nameColor = isCheerBot
              ? "#F7B731"
              : isAutoDj
              ? "#9AD7FF"
              : isSystem
              ? "#FF8A8A"
              : getNameColor(name);

            const avatarBg = isCheerBot
              ? "linear-gradient(135deg, rgba(247,183,49,0.95), rgba(255,80,80,0.85))"
              : isAutoDj
              ? "linear-gradient(135deg, rgba(55,126,255,0.95), rgba(174,69,255,0.88))"
              : "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))";

            return (
              <div
                key={`${m?.id || "m"}-${m?.ts || idx}-${idx}`}
                className="chatbox-msg"
                style={{
                  display: "flex",
                  gap: 9,
                  alignItems: "flex-start",
                  marginBottom: 9,
                  lineHeight: 1.35,
                  padding: isCheerBot || isAutoDj ? "8px 10px" : "2px 0",
                  borderRadius: isCheerBot || isAutoDj ? 12 : 0,
                  background: isCheerBot
                    ? "linear-gradient(135deg, rgba(247,183,49,0.18), rgba(255,255,255,0.05))"
                    : isAutoDj
                    ? "linear-gradient(135deg, rgba(55,126,255,0.18), rgba(174,69,255,0.16))"
                    : "transparent",
                  border: isCheerBot
                    ? "1px solid rgba(247,183,49,0.24)"
                    : isAutoDj
                    ? "1px solid rgba(120,170,255,0.28)"
                    : "none",
                  boxShadow: isAutoDj ? "0 8px 24px rgba(0,0,0,0.20)" : "none",
                }}
              >
                <span
                  style={{
                    width: 26,
                    height: 26,
                    minWidth: 26,
                    borderRadius: 999,
                    overflow: "visible",
                    position: "relative",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: avatarBg,
                    border: "1px solid rgba(255,255,255,0.14)",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.26)",
                    color: "white",
                    fontSize: avatar.label === "DJ" ? 9 : 12,
                    fontWeight: 950,
                    letterSpacing: avatar.label === "DJ" ? 0.2 : 0,
                    textTransform: "uppercase",
                    marginTop: 1,
                  }}
                  title={name}
                >
                  {avatar.type === "image" ? (
                    <img
                      src={avatar.src}
                      alt=""
                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      onError={(e) => {
                        const parent = e.currentTarget.parentElement;
                        e.currentTarget.remove();
                        if (parent) parent.textContent = avatar.label;
                      }}
                    />
                  ) : (
                    avatar.label
                  )}
                </span>

                <span style={{ minWidth: 0, flex: 1 }}>
                  {canOpenProfile ? (
                    <a
                      href={`/u/${encodeURIComponent(profileUsername)}`}
                      title={`View @${profileUsername}'s profile`}
                      style={{
                        fontWeight: 900,
                        opacity: 0.98,
                        color: nameColor,
                        textShadow: "0 1px 0 rgba(0,0,0,0.35)",
                        whiteSpace: "nowrap",
                        marginRight: 7,
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                    >
                      {name}
                    </a>
                  ) : (
                    <span
                      style={{
                        fontWeight: 900,
                        opacity: 0.98,
                        color: nameColor,
                        textShadow: "0 1px 0 rgba(0,0,0,0.35)",
                        whiteSpace: "nowrap",
                        marginRight: 7,
                      }}
                    >
                      {name}
                    </span>
                  )}
                  <span style={{ opacity: 0.95, wordBreak: "break-word" }}>
                    {renderChatText(msg)}
                  </span>
                </span>

                {canModerateMessage && (
                  <button
                    type="button"
                    onClick={() => {
                      setBlockedOpen(false);
                      setViewerOpen(false);
                      setReportType("");
                      setReportReason("harassment");
                      setReportDetails("");
                      setModerationTarget({
                        userId: targetUserId,
                        username: String(name || "Member"),
                        messageId: String(m?.id || ""),
                        messageText: String(msg || ""),
                        messageTs: Number(m?.ts || 0) || 0,
                      });
                    }}
                    title={`Report or block ${name}`}
                    aria-label={`Report or block ${name}`}
                    style={{
                      width: 28,
                      minWidth: 28,
                      height: 28,
                      borderRadius: 9,
                      border: "1px solid transparent",
                      background: "transparent",
                      color: "rgba(255,255,255,0.72)",
                      cursor: "pointer",
                      fontWeight: 950,
                      fontSize: 18,
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ⋮
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {moderationNotice ? (
        <div
          style={{
            marginTop: 8,
            padding: "9px 11px",
            borderRadius: 11,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(20,20,24,0.92)",
            color: "white",
            fontSize: 12,
            fontWeight: 750,
          }}
        >
          {moderationNotice}
        </div>
      ) : null}

      {moderationTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Chat moderation options"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.72)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: "18px 12px calc(18px + env(safe-area-inset-bottom, 0px))",
          }}
          onClick={closeModeration}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 460,
              borderRadius: 20,
              border: "1px solid rgba(255,255,255,0.15)",
              background: "linear-gradient(180deg, rgba(20,20,25,0.99), rgba(11,11,14,0.99))",
              color: "white",
              boxShadow: "0 24px 70px rgba(0,0,0,0.66)",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 950, fontSize: 17 }}>
                  {reportType ? `Report ${moderationTarget.username}` : moderationTarget.username}
                </div>
                {moderationTarget.messageText ? (
                  <div
                    style={{
                      marginTop: 6,
                      padding: "8px 10px",
                      borderRadius: 10,
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      fontSize: 12,
                      opacity: 0.82,
                      maxHeight: 72,
                      overflow: "hidden",
                      wordBreak: "break-word",
                    }}
                  >
                    “{moderationTarget.messageText}”
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={closeModeration}
                disabled={moderationBusy}
                aria-label="Close moderation options"
                style={{
                  width: 30,
                  height: 30,
                  minWidth: 30,
                  borderRadius: 999,
                  border: "none",
                  background: "rgba(255,255,255,0.08)",
                  color: "white",
                  cursor: moderationBusy ? "wait" : "pointer",
                  fontWeight: 950,
                }}
              >
                ×
              </button>
            </div>

            {!reportType ? (
              <div style={{ display: "grid", gap: 9, marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setReportType("message")}
                  style={{
                    minHeight: 44,
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.07)",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: 900,
                    textAlign: "left",
                    padding: "0 13px",
                  }}
                >
                  🚩 Report this message
                </button>
                <button
                  type="button"
                  onClick={() => setReportType("user")}
                  style={{
                    minHeight: 44,
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.12)",
                    background: "rgba(255,255,255,0.07)",
                    color: "white",
                    cursor: "pointer",
                    fontWeight: 900,
                    textAlign: "left",
                    padding: "0 13px",
                  }}
                >
                  🚩 Report this user
                </button>
                <button
                  type="button"
                  onClick={blockModerationTarget}
                  disabled={moderationBusy}
                  style={{
                    minHeight: 44,
                    borderRadius: 12,
                    border: "1px solid rgba(255,90,90,0.24)",
                    background: "rgba(210,45,45,0.14)",
                    color: "white",
                    cursor: moderationBusy ? "wait" : "pointer",
                    fontWeight: 900,
                    textAlign: "left",
                    padding: "0 13px",
                  }}
                >
                  🛡️ Block {moderationTarget.username}
                </button>
              </div>
            ) : (
              <div style={{ display: "grid", gap: 11, marginTop: 14 }}>
                <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 850 }}>
                  Reason
                  <select
                    value={reportReason}
                    onChange={(event) => setReportReason(event.target.value)}
                    disabled={moderationBusy}
                    style={{
                      width: "100%",
                      minHeight: 42,
                      borderRadius: 11,
                      border: "1px solid rgba(255,255,255,0.13)",
                      background: "rgba(255,255,255,0.08)",
                      color: "white",
                      padding: "0 10px",
                      outline: "none",
                    }}
                  >
                    {REPORT_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value} style={{ color: "black" }}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label style={{ display: "grid", gap: 6, fontSize: 12, fontWeight: 850 }}>
                  Extra details (optional)
                  <textarea
                    value={reportDetails}
                    onChange={(event) => setReportDetails(event.target.value.slice(0, 600))}
                    disabled={moderationBusy}
                    placeholder="Anything else we should know?"
                    rows={3}
                    style={{
                      width: "100%",
                      resize: "vertical",
                      borderRadius: 11,
                      border: "1px solid rgba(255,255,255,0.13)",
                      background: "rgba(255,255,255,0.08)",
                      color: "white",
                      padding: 10,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </label>

                <div style={{ display: "flex", gap: 9, marginTop: 2 }}>
                  <button
                    type="button"
                    onClick={() => setReportType("")}
                    disabled={moderationBusy}
                    style={{
                      flex: 1,
                      minHeight: 44,
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,0.11)",
                      background: "rgba(255,255,255,0.06)",
                      color: "white",
                      cursor: moderationBusy ? "wait" : "pointer",
                      fontWeight: 900,
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={submitReport}
                    disabled={moderationBusy}
                    style={{
                      flex: 1.4,
                      minHeight: 44,
                      borderRadius: 12,
                      border: "1px solid rgba(255,90,90,0.28)",
                      background: "linear-gradient(135deg, rgba(220,48,48,0.88), rgba(165,35,85,0.88))",
                      color: "white",
                      cursor: moderationBusy ? "wait" : "pointer",
                      fontWeight: 950,
                    }}
                  >
                    {moderationBusy ? "Submitting..." : "Submit report"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showEmoji && (
        <div
          className="emoji-panel"
          style={{
            position: "absolute",
            left: 12,
            bottom: 62,
            zIndex: 60,
            width: "min(340px, calc(100% - 24px))",
            maxHeight: "min(440px, 58vh)",
            display: "block",
            gridTemplateColumns: "none",
            gap: 0,
            overflowY: "auto",
            background: "rgba(0,0,0,0.96)",
            border: "1px solid rgba(255,255,255,0.14)",
            borderRadius: 14,
            padding: 10,
            boxShadow: "0 14px 35px rgba(0,0,0,0.55)",
          }}
        >
          <div
            style={{
              margin: "2px 4px 8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 900,
                letterSpacing: 0.7,
                opacity: 0.78,
                textTransform: "uppercase",
              }}
            >
              GA Emotes
            </span>
            <button
              type="button"
              onClick={() => {
                setShowEmoji(false);
                setPointsOpen(true);
              }}
              title="Open GAS Points rewards"
              style={{
                minHeight: 28,
                borderRadius: 999,
                border: "1px solid rgba(255,214,92,0.25)",
                background: "rgba(255,184,0,0.10)",
                color: "#ffd65c",
                padding: "0 9px",
                fontSize: 10,
                fontWeight: 950,
                cursor: "pointer",
              }}
            >
              ⛽ {pointsAccount ? gasPointsBalance.toLocaleString() : "…"} Points
            </button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 7,
            }}
          >
            {PLATFORM_EMOTES.map((emote) => (
              <button
                key={emote.key}
                type="button"
                onClick={() => insertPlatformEmote(emote.code)}
                disabled={!gasChatEmotesFree && gasPointsBalance < gasChatEmoteCost}
                title={
                  gasChatEmotesFree
                    ? `${emote.label} • member perk: free`
                    : `${emote.label} • ${gasChatEmoteCost} GAS Points when sent`
                }
                aria-label={`Use ${emote.label} emote`}
                style={{
                  minWidth: 0,
                  height: 70,
                  cursor:
                    !gasChatEmotesFree && gasPointsBalance < gasChatEmoteCost
                      ? "not-allowed"
                      : "pointer",
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.045)",
                  padding: 5,
                  borderRadius: 11,
                  color: "white",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <img
                  src={emote.src}
                  alt=""
                  style={{
                    width: 45,
                    height: 45,
                    maxWidth: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
                <span
                  style={{
                    marginTop: -2,
                    fontSize: 8,
                    lineHeight: 1,
                    color: gasChatEmotesFree ? "#8cffb2" : "#ffd65c",
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                  }}
                >
                  {gasChatEmotesFree ? "MEMBER FREE" : `${gasChatEmoteCost} PTS`}
                </span>
              </button>
            ))}
          </div>

          <div
            style={{
              margin: "12px 4px 8px",
              paddingTop: 10,
              borderTop: "1px solid rgba(255,255,255,0.09)",
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: 0.7,
              opacity: 0.78,
              textTransform: "uppercase",
            }}
          >
            Emojis
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
              gap: 5,
            }}
          >
            {QUICK_EMOJIS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => insertEmoji(e)}
                style={{
                  minWidth: 0,
                  cursor: "pointer",
                  border: "none",
                  background: "transparent",
                  fontSize: 18,
                  padding: 6,
                  borderRadius: 10,
                  color: "white",
                }}
                title={e}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
      )}

      <GasPointsStoreModal
        open={pointsOpen}
        onClose={() => setPointsOpen(false)}
        channelKey={channelKey}
        account={pointsAccount}
        onAccountChange={applyPointsAccount}
      />

      {cheerOpen && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 70,
            borderRadius: 14,
            background: "rgba(0,0,0,0.60)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
          onClick={() => {
            if (!cheerBusy) setCheerOpen(false);
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "linear-gradient(180deg, rgba(14,14,18,0.98), rgba(10,10,12,0.96))",
              boxShadow: "0 18px 50px rgba(0,0,0,0.55)",
              padding: 16,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 18 }}>Send GAS Cheers</div>
                <div style={{ opacity: 0.8, fontSize: 12, marginTop: 4 }}>
                  1 GAS Cheer = 1¢ • minimum {cheerConfig.minBits} Cheers
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!cheerBusy) setCheerOpen(false);
                }}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "white",
                  fontWeight: 900,
                  cursor: cheerBusy ? "wait" : "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ marginTop: 12, fontSize: 12, opacity: 0.82 }}>
              Supporting <span style={{ fontWeight: 800 }}>{channelKey}</span>
              {cheerConfig.totalBits > 0 ? ` • ${cheerConfig.totalBits.toLocaleString()} GAS Cheers sent so far` : ""}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginTop: 14 }}>
              {CHEER_PRESETS.map((value) => {
                const active = Number(cheerBits) === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setCheerBits(value)}
                    style={{
                      height: 42,
                      borderRadius: 12,
                      border: active
                        ? "1px solid rgba(255,255,255,0.30)"
                        : "1px solid rgba(255,255,255,0.10)",
                      background: active
                        ? "linear-gradient(135deg, rgba(0,160,255,0.30), rgba(255,70,180,0.28))"
                        : "rgba(255,255,255,0.05)",
                      color: "white",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    {value}
                  </button>
                );
              })}
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ display: "block", fontSize: 12, opacity: 0.82, marginBottom: 6 }}>
                Custom amount
              </label>
              <input
                type="number"
                min={cheerConfig.minBits}
                max={cheerConfig.maxBits}
                step="1"
                value={cheerBits}
                onChange={(e) => setCheerBits(e.target.value)}
                style={{
                  width: "100%",
                  height: 44,
                  borderRadius: 12,
                  padding: "0 12px",
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.06)",
                  color: "white",
                  outline: "none",
                }}
              />
            </div>

            <div style={{ marginTop: 14 }}>
              <label style={{ display: "block", fontSize: 12, opacity: 0.82, marginBottom: 6 }}>
                Message (optional)
              </label>
              <textarea
                value={cheerMessage}
                onChange={(e) => setCheerMessage(e.target.value.slice(0, 500))}
                rows={3}
                placeholder="Say something to the streamer..."
                style={{
                  width: "100%",
                  borderRadius: 12,
                  padding: 12,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.06)",
                  color: "white",
                  outline: "none",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ marginTop: 12, fontSize: 13, opacity: 0.88 }}>
              Total: <span style={{ fontWeight: 900 }}>${(Math.max(0, Number(cheerBits || 0)) / 100).toFixed(2)}</span>
            </div>

            {cheerError ? (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,90,90,0.24)",
                  background: "rgba(255,70,70,0.10)",
                  color: "rgba(255,230,230,0.98)",
                  fontSize: 13,
                }}
              >
                {cheerError}
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <button
                type="button"
                onClick={() => {
                  if (!cheerBusy) setCheerOpen(false);
                }}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.10)",
                  background: "rgba(255,255,255,0.06)",
                  color: "white",
                  fontWeight: 900,
                  cursor: cheerBusy ? "wait" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCheer}
                disabled={cheerBusy}
                style={{
                  flex: 1.2,
                  height: 44,
                  borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "linear-gradient(135deg, rgba(0,180,255,0.88), rgba(236,72,153,0.88))",
                  color: "white",
                  fontWeight: 900,
                  cursor: cheerBusy ? "wait" : "pointer",
                  opacity: cheerBusy ? 0.8 : 1,
                }}
              >
                {cheerBusy ? "Opening Checkout..." : "Continue"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        className="chatbox-compose"
        style={{
          marginTop: 10,
          display: "flex",
          gap: 8,
          alignItems: "flex-end",
          padding: "10px 10px calc(10px + var(--ga-bottom-safe-area, 0px))",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          borderRadius: 12,
          background: "rgba(0,0,0,0.25)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <button
          type="button"
          className="emoji-btn"
          onClick={() => setShowEmoji((s) => !s)}
          disabled={!signedIn}
          title="Emojis"
          style={{
            width: 40,
            minWidth: 40,
            height: 40,
            flex: "0 0 40px",
            display: "grid",
            placeItems: "center",
            borderRadius: 12,
            opacity: signedIn ? 1 : 0.5,
          }}
        >
          😀
        </button>

        <button
          type="button"
          className="chatbox-gas-btn"
          onClick={() => {
            setCheerError("");
            setCheerOpen(true);
          }}
          disabled={cheerButtonDisabled}
          title={cheerButtonTitle}
          style={{
            height: 40,
            flex: "0 0 auto",
            minWidth: 78,
            padding: "0 8px",
            borderRadius: 12,
            fontWeight: 900,
            fontSize: 11,
            border: "1px solid rgba(255,255,255,0.12)",
            background: cheerButtonDisabled
              ? "rgba(255,255,255,0.05)"
              : "linear-gradient(135deg, rgba(0,180,255,0.24), rgba(236,72,153,0.24))",
            color: "white",
            cursor: cheerButtonDisabled ? "not-allowed" : "pointer",
            opacity: cheerButtonDisabled ? 0.68 : 1,
            whiteSpace: "nowrap",
          }}
        >
          GAS Cheers
        </button>

        <textarea
          ref={messageInputRef}
          className="chatbox-text-input"
          value={text}
          rows={1}
          enterKeyHint="send"
          aria-label="Chat message"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (
              e.key === "Enter" &&
              !e.shiftKey &&
              !e.nativeEvent?.isComposing
            ) {
              e.preventDefault();
              submit();
            }
          }}
          onFocus={() => setMobileTypingMode(true)}
          onBlur={() => {
            setTimeout(() => {
              const activeEl = document.activeElement;
              setMobileTypingMode(activeEl === messageInputRef.current);
            }, 0);
          }}
          placeholder={signedIn ? "Type a message…" : "Sign in to chat…"}
          disabled={!signedIn}
          style={{
            flex: "1 1 0",
            minWidth: 0,
            width: 0,
            minHeight: 40,
            height: 40,
            maxHeight: 96,
            borderRadius: 12,
            padding: "9px 12px",
            border: "1px solid rgba(255,255,255,0.10)",
            background: "rgba(255,255,255,0.06)",
            color: "white",
            outline: "none",
            resize: "none",
            overflowY: "hidden",
            overflowX: "hidden",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            wordBreak: "break-word",
            lineHeight: 1.35,
            font: "inherit",
            fontSize: 16,
          }}
        />

        <button
          type="button"
          className="chatbox-send-btn"
          onClick={submit}
          disabled={!signedIn || !text.trim()}
          style={{
            height: 40,
            flex: "0 0 auto",
            minWidth: 60,
            padding: "0 12px",
            borderRadius: 12,
            fontWeight: 900,
            border: "1px solid rgba(255,255,255,0.12)",
            background:
              signedIn && text.trim()
                ? "rgba(255,255,255,0.12)"
                : "rgba(255,255,255,0.06)",
            color: "white",
            cursor: signedIn && text.trim() ? "pointer" : "not-allowed",
          }}
        >
          Send
        </button>
      </div>
    </div>
  );
}